import * as React from "react";
import {useState} from "react";
import {INTERNAL_ERROR_TYPE} from "../../../../lib/payments/PaymentController";
import {purchase} from "../../../../lib/payments/purchase";
import {getVerifyCardOptions, GPayAuthError} from "../../../../lib/payments/googlepay/onGoogleAuthorized";
import braintree from "braintree-web";
import {ThreeDSecureInfo, ThreeDSecureVerificationData} from "braintree-web/three-d-secure";
import {updateUrlParams} from "../../../../cart/CartIcon/CartIconPortal";
import {Button, ButtonStyles, ButtonType} from "../../../../lib/form/Button/Button";
import Spinner from "../../../../utils/loader/Spinner";
import {useCartContext} from "../../../../cart/CartContextProvider/CartContextProvider";
import {useTranslation} from "react-i18next";
import {useCheckoutContext} from "../../../CheckoutContextProvider/CheckoutContextProvider";
import {PurchaseButtonProps} from "./PurchaseButtonProps";
import {sentryAddBreadcrumb} from "../../../../../helpers/sentryHelper";
import {CheckoutStep} from "../../../Checkout";
import {useCustomerSelector} from "../../../../../store";

// TODO evtl ein bisschen aufräumen (threeDSecure verschieben)
const GooglePayPurchaseButton: React.FC<PurchaseButtonProps> = ({purchaseButtonErrors}) => {
  const {customer} = useCustomerSelector();
  const {braintreeData, setIsIntoCheckout, setViewState, viewState} = useCartContext();
  const {checkout} = useCheckoutContext();
  const {t} = useTranslation(["errorcodes", "translations"]);
  const [submitted, setSubmitted] = useState(false);

  const onPurchase = async () => {
    const errName = INTERNAL_ERROR_TYPE.GOOGLE_PAY.OnPurchase;
    const occurrence = "Google Pay Summary Button";
    if (!viewState.gPayData) {
      setIsIntoCheckout(false);

      return;
    }

    setSubmitted(true);

    try {
      // 'network-' vs. 'non-network-tokenized' payment data.
      // See <https://developer.paypal.com/braintree/docs/guides/3d-secure/client-side/javascript/v3/#using-3d-secure-with-google-pay>
      if (viewState.gPayData.tokenData.details.isNetworkTokenized) {
        sentryAddBreadcrumb(CheckoutStep.SUMMARY, checkout, "network tokenized, no 3ds needed", undefined, customer);
        await purchase({checkout, nonce: viewState.gPayData.tokenData.nonce, visitorId: customer.visitorId});
      } else {
        sentryAddBreadcrumb(CheckoutStep.SUMMARY, checkout, "not network tokenized, 3ds needed", undefined, customer);
        if (!braintreeData) {
          purchaseButtonErrors(new GPayAuthError("No btData given in Summary with GPay set", errName), occurrence);

          return;
        }

        // const {client, token} = await PaymentController.initBraintreeClient();
        // const client = await braintree.client.create({authorization: braintreeToken});
        const threeDSecure = await braintree.threeDSecure.create({
          client: braintreeData.client,
          version: 2,
        });

        threeDSecure.on("lookup-complete", (data: ThreeDSecureVerificationData, next) => {
          // INFO: This is all before the opening of the popup
          if (!data) {
            throw new GPayAuthError("No lookup-complete data before 3DSecure popup opening", errName);
          }
          const status = (data.paymentMethod.threeDSecureInfo as ThreeDSecureInfo & {status: string}).status;
          if (["authentication_unavailable", "unsupported_card"].includes(status)) {
            throw new Error("Invalid Credit Card Number");
          }
          if (!data.threeDSecureInfo.liabilityShiftPossible) {
            // this message is a known key in the `errorcodes.json`
            throw new Error("Invalid Credit Card Number");
          }
          if (typeof next !== "function") {
            throw new GPayAuthError("No `next()` method given in 'lookup-complete' callback", errName);
          }

          // this will open the popup
          next();
        });

        let wasCancelledByUser = false;
        threeDSecure.on("customer-canceled", () => {
          wasCancelledByUser = true;
        });

        const threeDSVerifyPayload = await threeDSecure.verifyCard(
          getVerifyCardOptions(viewState.gPayData, checkout.cartData.value.payable.amount),
        );

        if (!threeDSVerifyPayload.threeDSecureInfo.liabilityShifted) {
          setSubmitted(false);
          setViewState({...viewState, gPayData: undefined});
          if (wasCancelledByUser) {
            setIsIntoCheckout(false);
            updateUrlParams("cart");
          } else {
            purchaseButtonErrors(new GPayAuthError("Liability not shifted", errName), "Google Pay Summary Button", {
              threeDSecureInfo: threeDSVerifyPayload.threeDSecureInfo,
            });
          }

          return;
        }

        await purchase({checkout, nonce: threeDSVerifyPayload.nonce, visitorId: customer.visitorId});
      }
    } catch (err) {
      purchaseButtonErrors(err as Error, occurrence);
    }
  };

  return (
    <Button
      action={onPurchase}
      styles={[ButtonStyles.wide]}
      type={submitted ? ButtonType.disabled : ButtonType.default}
    >
      {submitted ? <Spinner /> : t("checkout.purchaseNow")}
    </Button>
  );
};

export default GooglePayPurchaseButton;
